import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { ListPagination } from '@/components/ListPagination';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { AssignVehicleTransactionSheet } from '@/components/vehicles/AssignVehicleTransactionSheet';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { Search, MoreHorizontal, ArrowRightLeft, X, Filter, Car } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useDebounce } from '@/hooks/useDebounce';
import { formatBusinessDate, formatBusinessTime, formatMoney } from '@/lib/format';
import { BOE_LINK_COLUMNS } from '@/components/vehicles/customsAllocation';

// ─── Types ───────────────────────────────────────────

const PAGE_SIZE = 15;
type TransactionStatus = 'ALL' | 'ASSIGNED' | 'UNASSIGNED';
type CurrencyFilter = 'USD' | 'GHS' | 'ALL';

const TYPE_LABELS: Record<string, string> = {
    cif_30: 'CIF 30%',
    cif_70: 'CIF 70%',
    tax_bill: 'Tax Bill',
    port_charges: 'Port Charges',
    landed_cost: 'Landed Cost',
    steering_changed: 'Steering',
    levy: 'CHA Membership Levy',
};

interface VehicleTransaction {
    id: number;
    vt_id: number | null;
    vehicle_id: number | null;
    vehicle_name: string | null;
    vehicle_registration: string | null;
    vehicle_color: string | null;
    vehicle_listing_url: string | null;
    vehicle_vin: string | null;
    vehicle_updated_at: string | null;
    transaction_type: string | null;
    amount: number;
    fee: number | null;
    currency: string | null;
    transaction_at: string;
    description: string | null;
    status: 'ASSIGNED' | 'UNASSIGNED';
    total_count: number;
    link_count: number;
}

interface VehicleTransactionAssignerProps {
    dateRange: { from: Date; to: Date };
}

// ─── Helpers ─────────────────────────────────────────

function getColorValue(color: string): string {
    const trimmed = color.trim();
    if (/^[0-9A-Fa-f]{6}$/.test(trimmed)) return `#${trimmed}`;
    if (trimmed.startsWith('#')) return trimmed;
    return trimmed;
}

/** Derive SBT photo URL from listing URL with cache buster */
function getSbtPhotoUrl(listingUrl: string | null, updatedAt?: string | null): string | null {
    if (!listingUrl) return null;
    const match = listingUrl.match(/sbtjapan\.com\/used-cars\/([A-Z0-9]+)/i);
    if (!match) return null;
    const cacheBust = updatedAt ? `?v=${new Date(updatedAt).getTime()}` : '';
    return `https://img.sbtjapan.com/img/carphoto/${match[1]}/normal/f.jpg${cacheBust}`;
}

function getStockId(url: string | null): string | null {
    if (!url) return null;
    const segments = url.replace(/\/+$/, '').split('/');
    return segments[segments.length - 1] || null;
}

// ─── Component ───────────────────────────────────────

export default function VehicleTransactionAssigner({ dateRange }: VehicleTransactionAssignerProps) {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<TransactionStatus>('ALL');
    const [currencyFilter, setCurrencyFilter] = useState<CurrencyFilter>('USD');
    const [currentPage, setCurrentPage] = useState(1);
    const debouncedSearch = useDebounce(searchTerm, 300);

    // Sheet state
    const [assignSheetOpen, setAssignSheetOpen] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState<VehicleTransaction | null>(null);
    const [unassignRecord, setUnassignRecord] = useState<VehicleTransaction | null>(null);
    const [reassignRecord, setReassignRecord] = useState<VehicleTransaction | null>(null);

    const fromDate = format(dateRange.from, 'yyyy-MM-dd');
    const toDate = format(dateRange.to, 'yyyy-MM-dd');

    // Reset page on filter change
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, fromDate, toDate, statusFilter, currencyFilter]);

    const currencyParam = currencyFilter === 'ALL' ? null : currencyFilter;

    // ── Fetch paginated transactions ──
    const { data: recordsData, isLoading } = useQuery({
        queryKey: ['all-vehicle-transactions', currentPage, debouncedSearch, fromDate, toDate, statusFilter, currencyParam],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_all_vehicle_transactions', {
                p_from_date: fromDate,
                p_to_date: toDate,
                p_search: debouncedSearch || null,
                p_page: currentPage,
                p_page_size: PAGE_SIZE,
                p_status: statusFilter,
                p_currency: currencyParam,
            });
            if (error) throw error;
            return (data || []) as VehicleTransaction[];
        },
    });

    const transactions = recordsData || [];
    const totalCount = transactions.length > 0 ? transactions[0].total_count : 0;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    // ── Stats ──
    // ONE SUM PER CURRENCY. Given a null currency the RPC adds USD CIF payments to GHS customs
    // payments into one amount, so "All" asks it once per currency: the counts add up across the
    // two, the amounts are printed apart.
    const { data: statsData } = useQuery({
        queryKey: ['vehicle-transaction-stats', fromDate, toDate, currencyParam],
        queryFn: async () => {
            const perCurrency = await Promise.all((currencyParam ? [currencyParam] : ['USD', 'GHS']).map(async currency => {
                const { data, error } = await supabase.rpc('get_vehicle_transaction_stats', {
                    p_from_date: fromDate,
                    p_to_date: toDate,
                    p_currency: currency,
                });
                if (error) throw error;
                const s = data?.[0] || { total: 0, total_amount: 0, assigned_count: 0, unassigned_count: 0, assigned_amount: 0 };
                return {
                    currency,
                    total: s.total,
                    assigned: s.assigned_count,
                    unassigned: s.unassigned_count,
                    assignedAmount: s.assigned_amount,
                    avgAssigned: s.assigned_count > 0 ? s.assigned_amount / s.assigned_count : 0,
                };
            }));
            const [primary, ...others] = perCurrency;
            return {
                total: perCurrency.reduce((sum, c) => sum + c.total, 0),
                assigned: perCurrency.reduce((sum, c) => sum + c.assigned, 0),
                unassigned: perCurrency.reduce((sum, c) => sum + c.unassigned, 0),
                assignedAmount: primary.assignedAmount,
                avgAssigned: primary.avgAssigned,
                others,
            };
        },
    });

    // ── Unassign mutation ──
    const unassignMutation = useMutation({
        mutationFn: async ({ transactionId, transactionType }: { transactionId: number; transactionType: string }) => {
            // Clear the BOE link column first: if this fails partway, the
            // vehicle_transactions rows are untouched and stay visible/recoverable
            // instead of leaving a stale link pointing at an unassigned transaction.
            const boeColumn = BOE_LINK_COLUMNS[transactionType];
            if (boeColumn) {
                const { error: boeError } = await supabase
                    .from('bills_of_entry')
                    .update({ [boeColumn]: null })
                    .eq(boeColumn, transactionId);
                if (boeError) throw boeError;
            }

            const { error } = await supabase
                .from('vehicle_transactions')
                .delete()
                .eq('transaction_id', transactionId)
                .eq('transaction_type', transactionType as Database['public']['Enums']['vehicle_transaction_type']);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Transaction unassigned');
            queryClient.invalidateQueries({ queryKey: ['all-vehicle-transactions'] });
            queryClient.invalidateQueries({ queryKey: ['vehicle-transaction-stats'] });
            setUnassignRecord(null);
        },
        onError: (e: Error) => {
            toast.error(e.message);
        },
    });

    // ── Handlers ──
    const handleAssignSingle = (t: VehicleTransaction) => {
        setSelectedTransaction(t);
        setReassignRecord(null);
        setAssignSheetOpen(true);
    };

    const handleReassign = (t: VehicleTransaction) => {
        setReassignRecord(t);
        setSelectedTransaction(null);
        setAssignSheetOpen(true);
    };

    const handleSheetClose = (open: boolean) => {
        setAssignSheetOpen(open);
        if (!open) {
            setSelectedTransaction(null);
            setReassignRecord(null);
            queryClient.invalidateQueries({ queryKey: ['all-vehicle-transactions'] });
            queryClient.invalidateQueries({ queryKey: ['vehicle-transaction-stats'] });
        }
    };

    const activeTransaction = reassignRecord || selectedTransaction;
    const statsCurrency = currencyFilter === 'ALL' ? 'USD' : currencyFilter;
    const unassignSharedCount = unassignRecord?.link_count || 1;

    return (
        <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid gap-4 md:grid-cols-5">
                <StatCard
                    title="Assigned Amount"
                    value={<CurrencyDisplay amount={statsData?.assignedAmount || 0} currency={statsCurrency} />}
                    subtitle={statsData?.others.map(o => `and ${formatMoney(o.assignedAmount, o.currency)}`).join(' · ') || undefined}
                    description="Total value of transactions assigned to vehicles."
                />
                <StatCard
                    title="Avg Assigned"
                    value={<CurrencyDisplay amount={statsData?.avgAssigned || 0} currency={statsCurrency} />}
                    subtitle={statsData?.others.map(o => `and ${formatMoney(o.avgAssigned, o.currency)}`).join(' · ') || undefined}
                    description="Mean value of assigned transactions."
                />
                <StatCard
                    title="Total Transactions"
                    value={statsData?.total || 0}
                    description={`${currencyFilter === 'ALL' ? 'All-currency' : statsCurrency} DEBIT transactions in the date range.`}
                />
                <StatCard
                    title="Assigned"
                    value={statsData?.assigned || 0}
                    description="Transactions linked to a vehicle."
                />
                <StatCard
                    title="Unassigned"
                    value={statsData?.unassigned || 0}
                    description="Transactions not yet linked to any vehicle."
                />
            </div>

            {/* Filters & Pagination */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex flex-1 items-center gap-2 w-full">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search by description, amount..."
                            className="pl-9"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <Select
                        value={statusFilter}
                        onValueChange={(val) => setStatusFilter(val as TransactionStatus)}
                    >
                        <SelectTrigger className="w-[160px]">
                            <div className="flex items-center gap-2">
                                <Filter className="h-4 w-4 text-muted-foreground" />
                                <SelectValue placeholder="Status" />
                            </div>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="ALL">All Status</SelectItem>
                            <SelectItem value="ASSIGNED">Assigned</SelectItem>
                            <SelectItem value="UNASSIGNED">Unassigned</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select
                        value={currencyFilter}
                        onValueChange={(val) => setCurrencyFilter(val as CurrencyFilter)}
                    >
                        <SelectTrigger className="w-[110px] shrink-0">
                            <SelectValue placeholder="Currency" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="GHS">GHS</SelectItem>
                            <SelectItem value="ALL">All</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {totalPages > 0 && (
                    <ListPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        totalItems={totalCount}
                    />
                )}
            </div>

            {/* Table */}
            <div>
                {isLoading ? (
                    <Skeleton className="h-48 w-full" />
                ) : transactions.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                                <TableHead className="pl-8">Vehicle</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead className="w-12"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {transactions.map((t) => {
                                const isUnassigned = t.status === 'UNASSIGNED';
                                const isShared = t.link_count > 1;
                                return (
                                    <TableRow key={`${t.id}-${t.vt_id || 'u'}`}>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <span>{formatBusinessDate(t.transaction_at)}</span>
                                                <span className="text-xs text-muted-foreground">
                                                    {formatBusinessTime(t.transaction_at)}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="max-w-[320px]">
                                            <span className="text-sm break-words whitespace-pre-wrap">
                                                {t.description || '-'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex flex-col items-end">
                                                <span className={`font-medium ${isUnassigned ? 'text-red-600 dark:text-red-400' : ''}`}>
                                                    {formatMoney(t.amount, t.currency)}
                                                </span>
                                                {t.fee != null && t.fee > 0 && (
                                                    <span className="text-xs text-orange-500">
                                                        Fee: {formatMoney(t.fee, t.currency)}
                                                    </span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="pl-8">
                                            {t.vehicle_id ? (
                                                <div className="flex items-center gap-2">
                                                    {(() => {
                                                        const photoUrl = getSbtPhotoUrl(t.vehicle_listing_url, t.vehicle_updated_at);
                                                        if (photoUrl) {
                                                            return (
                                                                <HoverCard openDelay={200} closeDelay={100}>
                                                                    <HoverCardTrigger asChild>
                                                                        <img
                                                                            src={photoUrl}
                                                                            alt=""
                                                                            className="h-8 w-8 rounded-md border border-gray-200 object-cover shrink-0 cursor-zoom-in"
                                                                            onError={(e) => {
                                                                                const el = e.currentTarget;
                                                                                el.style.display = 'none';
                                                                                el.nextElementSibling?.classList.remove('hidden');
                                                                            }}
                                                                        />
                                                                    </HoverCardTrigger>
                                                                    <HoverCardContent side="left" align="center" className="w-auto p-1" sideOffset={8}>
                                                                        <img src={photoUrl} alt={t.vehicle_name || ''} className="w-[480px] rounded-md" />
                                                                    </HoverCardContent>
                                                                </HoverCard>
                                                            );
                                                        }
                                                        return null;
                                                    })()}
                                                    <div
                                                        className={`h-8 w-8 rounded-md border border-gray-200 shrink-0${getSbtPhotoUrl(t.vehicle_listing_url, t.vehicle_updated_at) ? ' hidden' : ''}`}
                                                        style={{ backgroundColor: getColorValue(t.vehicle_color || '') || '#e5e7eb' }}
                                                    />
                                                    <div className="flex flex-col">
                                                        <span className="font-medium text-sm">{t.vehicle_name}</span>
                                                        <div className="flex items-center gap-2">
                                                            {t.vehicle_vin && (
                                                                <code className="text-[11px] text-muted-foreground font-mono">{t.vehicle_vin}</code>
                                                            )}
                                                            {(() => {
                                                                const stockId = getStockId(t.vehicle_listing_url);
                                                                return stockId ? <code className="text-[11px] text-muted-foreground/80 font-mono">{stockId}</code> : null;
                                                            })()}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {t.transaction_type ? (
                                                <Badge variant="outline" className="text-xs">
                                                    {TYPE_LABELS[t.transaction_type] || t.transaction_type}
                                                </Badge>
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {isUnassigned ? (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleAssignSingle(t)}
                                                >
                                                    Assign
                                                </Button>
                                            ) : (
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-8 w-8">
                                                            <MoreHorizontal className="h-4 w-4" />
                                                            <span className="sr-only">Actions</span>
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        {!isShared && (
                                                            <DropdownMenuItem onClick={() => handleReassign(t)}>
                                                                <ArrowRightLeft className="h-4 w-4 mr-2" />
                                                                Reassign
                                                            </DropdownMenuItem>
                                                        )}
                                                        <DropdownMenuItem
                                                            onClick={() => setUnassignRecord(t)}
                                                            className="text-destructive focus:text-destructive"
                                                        >
                                                            <X className="h-4 w-4 mr-2" />
                                                            Unassign
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                ) : (
                    <div className="text-center py-12 border-2 border-dashed rounded-lg">
                        <Car className="h-8 w-8 mx-auto mb-3 text-muted-foreground/40" />
                        <p className="text-muted-foreground">No transactions found</p>
                        <p className="text-sm text-muted-foreground/70 mt-1">
                            {searchTerm ? 'Try a different search term' : `No ${currencyFilter === 'ALL' ? '' : statsCurrency + ' '}debit transactions in this date range`}
                        </p>
                    </div>
                )}
            </div>

            {/* Unassign Confirmation */}
            <AlertDialog open={!!unassignRecord} onOpenChange={(open) => !open && setUnassignRecord(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Unassign Transaction{unassignSharedCount > 1 ? 's' : ''}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {unassignSharedCount > 1
                                ? `This transaction is shared across ${unassignSharedCount} vehicles, including ${unassignRecord?.vehicle_name}. This will remove it from all ${unassignSharedCount} of them.`
                                : `This will remove the transaction from ${unassignRecord?.vehicle_name}.`}
                            {' '}It will appear as &lsquo;Unassigned&rsquo; and can be reassigned later.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => unassignRecord?.transaction_type && unassignMutation.mutate({
                                transactionId: unassignRecord.id,
                                transactionType: unassignRecord.transaction_type,
                            })}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Unassign
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Assign/Reassign Sheet */}
            {(assignSheetOpen && activeTransaction) && (
                <AssignVehicleTransactionSheet
                    open={assignSheetOpen}
                    onOpenChange={handleSheetClose}
                    transaction={{
                        id: activeTransaction.id,
                        amount: activeTransaction.amount,
                        currency: activeTransaction.currency,
                        transaction_at: activeTransaction.transaction_at,
                        description: activeTransaction.description,
                    }}
                    existingVtId={reassignRecord?.vt_id}
                    existingTransactionType={reassignRecord?.transaction_type}
                />
            )}
        </div>
    );
}
